import { Bounds } from '../search-es/types';
import {
  AVAILABLE_STATUS,
  DATE_DESCENDING_SORT,
  NOT_AVAILABLE_SOLD_STATUS,
  Sort,
} from 'contexts/preferences/listing-params/types';
import { defaultHomeType } from 'constants/listing-components';
import { getGeohashPrecisionFromZoom } from 'components/search-es/geo_helper';
import { getGoSearchHost, isClientSide, isServerSide } from 'utils/host-config';
import { SearchByAreaFilterType } from 'data/search/area/types';
import { SearchApi, SearchApiType } from 'data/search/api';
import { AddressType, AreaPageListing, AreaPageListingResponse, SortBy } from '@zoocasa/go-search';
import { GeoSearchFilter } from 'data/search/map/types';
import { cloneDeep } from 'lodash';
import deepmerge from 'deepmerge';
import defaultListingParams from 'contexts/preferences/listing-params/defaults';
import { ThemeName as TenantName } from 'themes';
import { CountryCode, CountryCodeList } from 'types/countries';
import { getAddressesBySlug } from 'data/addresses/go-search/fetcher';
import { AddressHierarchyPath, createAddressHierarchyPath } from 'data/addresses';
import { getSortBy } from 'utils/sort';
import { US_ADDRESS, CANADA_ADDRESS } from 'constants/address';
import { sanitizeProvinceOrStateCode } from 'utils/sanitize';
import { sanitizeName } from 'utils/sanitize';
import { getTenantById, themeNameToTenantId } from '@tenants';

const DefaultSearchApi: SearchApiType = Object.freeze(SearchApi.create(getGoSearchHost(!isClientSide()), false));

//#region Types
export type ListingsQueryType = {
  /** The country to use as a fallback if no slug is provided or no listings are found for the given slug */
  country: CountryCode;
  /** The slug representing the location to fetch listings for */
  slug?: string;
  /** The filter criteria for the search */
  filter: SearchByAreaFilterType;
  /** The number of listings to fetch per page */
  pageSize: number;
  /** Whether to use the legacy search filter */
  useLegacySearchFilter?: boolean;
  /** Whether the request is made from the client side */
  isClient?: boolean;
};

/**
 * Represents the result of fetching listings for an area with a fallback mechanism.
 *
 * The address property will be one of the addresses in the addresses property or the country address if no addresses were found.
 */
export type AreaPageListingsWithFallback = {
  /** The list of addresses associated with an slug in hierarchical order. */
  addresses?: AddressHierarchyPath;
  /** The listings fetched from the search API. */
  areaPageListingResponse?: AreaPageListingResponse;
  /** Whether the address used to fetch listings is an expanded area. */
  isExpandedArea: boolean;
};

export type HomePageListings = {
  /**
   * The listings fetched for the home page.
   */
  listings: AreaPageListing[];

  /**
   * The total count of listings fetched for the home page.
   */
  totalCount: number;

  /**
   * Whether the response is a top level fallback (country level) response.
   */
  isExpandedArea: boolean;
};
//#endregion


/**
 * Fetches home page listings based on the provided query parameters.
 *
 * It will fallback to fetching listings for the entire specified country if no slug is provided.
 *
 * @param query The query parameters for fetching home page listings.
 * @returns The fetched home page listings, including the total count and whether the response is a top level fallback.
 */
export const fetchHomePageListings = async (query: ListingsQueryType): Promise<HomePageListings> => {
  const { country, slug, filter, pageSize, isClient = false, useLegacySearchFilter = false } = query;
  const pageNumber = 1;
  const sort = DATE_DESCENDING_SORT;
  const searchApi = SearchApi.create(getGoSearchHost(!isClient), useLegacySearchFilter);
  const needCountryFallback = slug?.length === 0;

  // If no slug is provided, fetch listings for the entire specified country
  if (needCountryFallback) {
    const { areaPageListingResponse } = await getListingsForCountry(country, filter, sort, pageNumber, pageSize, searchApi);
    const listings = areaPageListingResponse?.data || [];
    const totalCount = areaPageListingResponse?.TotalCount || 0;

    const homePageListings: HomePageListings = {
      listings,
      totalCount,
      isExpandedArea: true,
    };
    return homePageListings;
  }

  const { areaPageListingResponse, isExpandedArea } = await getListingsWithCountryFallback(slug, filter, sort, pageNumber, pageSize, country, searchApi);
  const listings = areaPageListingResponse?.data || [];
  const totalCount = areaPageListingResponse?.TotalCount || 0;

  const homePageListings: HomePageListings = {
    listings,
    totalCount,
    isExpandedArea,
  };
  return homePageListings;
};

export const fetchHomePageListingsWithBoundary = (bounds: Bounds, tenantName: TenantName, isSold?: boolean, useLegacySearchFilter = false) => {
  const tenant = getTenantById(themeNameToTenantId(tenantName));
  const countries = tenant.localization.countries;

  let countryCode: CountryCode | undefined; // FIXME: API does not support multiple countries yet
  if (countries.includes(CountryCodeList.UNITED_STATES) && countries.includes(CountryCodeList.CANADA)) {
    countryCode = undefined;
  } else {
    countryCode = countries?.[0] as CountryCode;
  }

  const zoomLevel = 10;
  const pageNumber = '1';
  const sortBy = SortBy.DateDesc;
  const size = isSold ? 10 : 15;
  const precision = getGeohashPrecisionFromZoom(zoomLevel);
  const searchApi = SearchApi.create(getGoSearchHost(isServerSide()), useLegacySearchFilter);
  const filter: GeoSearchFilter = cloneDeep(deepmerge(defaultListingParams.filter, {
    status: isSold ? NOT_AVAILABLE_SOLD_STATUS : AVAILABLE_STATUS,
    homeType: defaultHomeType,
    countryCode,
    hasImage: true,
  }));

  return (searchApi.searchListingsInBoundary(bounds, precision, pageNumber, filter, sortBy, undefined, size));
};

/**
 * Fetch listings for a given country.
 *
 * @param country The country to fetch listings for.
 * @param filter The filter criteria for the search, including tenant information.
 * @param sort The sorting criteria for the listings.
 * @param pageNumber The page number for pagination.
 * @param pageSize The number of listings per page.
 * @param searchApi The search API instance to use for fetching listings. Defaults to {@link DefaultSearchApi}.
 *
 * @returns A promise that resolves to an {@link AreaPageListingsWithFallback} instance representing the fetched listings and related data.
 */
export async function getListingsForCountry(country: CountryCode, filter: SearchByAreaFilterType, sort: Sort, pageNumber: number, pageSize: number, searchApi: SearchApiType = DefaultSearchApi): Promise<AreaPageListingsWithFallback> {
  const areaPageListingResponse = await searchApi.searchByArea(country, '', '', '', filter, filter.tenant, getSortBy(sort), pageNumber, pageSize);
  const address = country === CountryCodeList.UNITED_STATES ? US_ADDRESS : CANADA_ADDRESS;
  return {
    addresses: createAddressHierarchyPath([address]),
    areaPageListingResponse,
    isExpandedArea: false,
  };
}

/**
 * Fetch listings for a given slug with a fallback mechanism.
 *
 * This function attempts to fetch area page listings based on the provided slug. If no listings are found for the given slug,
 * it progressively falls back to higher-level locations (e.g., neighbourhood, city, province/state, country) until listings are found.
 *
 * As a fallback, if no listings are found for the given slug, it will perform a search at the country level using the fallbackCountry.
 *
 * The function follows these steps:
 * 1. If the slug is empty or the fallbackCountry is not provided, return undefined.
 * 2. If the slug is not empty, retrieve addresses associated with the given slug.
 * 3. Iterate through the found addresses in descending hierarchical order (e.g., neighbourhood, city, province/state, country) until
 *    listings are found for an address.
 * 4. If no listings are found for any address, perform a final search at the country level using the fallbackCountry.
 *
 * Top level fallback (country level) responses are marked as {@link AreaPageListingsWithFallback.isExpandedArea} = true.
 *
 * @param slug The slug representing the location to search for listings.
 * @param filter The filter criteria for the search, including tenant information.
 * @param sort The sorting criteria for the listings.
 * @param pageNumber The page number for pagination.
 * @param pageSize The number of listings per page.
 * @param fallbackCountry The country code to use as a fallback if no listings are found for the given slug.
 * @param searchApi The search API instance to use for fetching listings. Defaults to DefaultSearchApi.
 *
 * @returns A promise that resolves to an {@link AreaPageListingsWithFallback} instance representing the fetched listings and related data.
 */
export async function getListingsWithCountryFallback(slug: string, filter: SearchByAreaFilterType, sort: Sort, pageNumber: number, pageSize: number, fallbackCountry: CountryCode, searchApi: SearchApiType = DefaultSearchApi): Promise<AreaPageListingsWithFallback | undefined> {

  if (slug?.length === 0 || fallbackCountry?.length === 0) {
    return undefined;
  }

  const addresses = await getAddressesBySlug(slug);
  if (addresses && addresses?.length > 0) {
    // Since the list of addresses is sorted hierarchically, we can assume the first address is the country.
    const country = addresses?.[0]?.country;
    const displayableAddresses = [...addresses].reverse().filter(address => address.addressType <= AddressType.ADDRESS_TYPE_NEIGHBOURHOOD);
    const addressHierarchyPath = createAddressHierarchyPath(displayableAddresses);
    for (let index = 0; index < displayableAddresses.length; index++) {
      const address = displayableAddresses[index];

      const provinceOrState = sanitizeProvinceOrStateCode(address.provinceOrState ?? '');
      const city = sanitizeName(address.subDivision ?? '');
      const neighbourhood = address.neighbourhood ?? '';
      const areaPageListingResponse = await searchApi.searchByArea(country, provinceOrState, city, neighbourhood, filter, filter.tenant, getSortBy(sort), pageNumber, pageSize);

      if (areaPageListingResponse.TotalCount > 0) {
        return { addresses: addressHierarchyPath, areaPageListingResponse, isExpandedArea: false };
      }
    }
  }

  // We couldn't find the address based on the fallback slug. Default to top most level ("country")
  return getListingsForCountry(fallbackCountry, filter, sort, pageNumber, pageSize, searchApi);
}